/**
 * Firebase Auth bootstrap.
 *
 * The config is fetched from the API's unauthenticated /config.json rather than
 * baked in at build time, so one image runs in every environment — otherwise
 * sta and prod would need separate builds differing only in a project id, and
 * the image tag would stop meaning "this commit".
 *
 * A Firebase web API key is a public project identifier, not a credential: it
 * identifies which project a sign-in request belongs to and authorizes nothing
 * on its own. Serving it to the browser is the intended design.
 */

import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';

let app = null;
let auth = null;
let runtimeConfig = null;

export async function initFirebase() {
  if (auth) return { auth, config: runtimeConfig };

  const response = await fetch('/config.json', { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`could not load runtime config (HTTP ${response.status})`);
  }
  runtimeConfig = await response.json();

  if (!runtimeConfig.firebase?.apiKey) {
    throw new Error(
      'the server did not return a Firebase config — set FIREBASE_API_KEY and FIREBASE_AUTH_DOMAIN'
    );
  }

  app = initializeApp({
    apiKey: runtimeConfig.firebase.apiKey,
    authDomain: runtimeConfig.firebase.authDomain,
    projectId: runtimeConfig.firebase.projectId,
  });
  auth = getAuth(app);

  // Survive a reload; a cap table session shouldn't evaporate on refresh.
  await setPersistence(auth, browserLocalPersistence);

  return { auth, config: runtimeConfig };
}

export function getFirebaseAuth() {
  if (!auth) throw new Error('initFirebase() has not completed');
  return auth;
}

export function getRuntimeConfig() {
  return runtimeConfig;
}

export function watchAuth(callback) {
  return onAuthStateChanged(getFirebaseAuth(), callback);
}

export async function signInWithPassword(email, password) {
  return signInWithEmailAndPassword(getFirebaseAuth(), email, password);
}

export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  // Always show the chooser — people administering several companies routinely
  // hold more than one Google identity.
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(getFirebaseAuth(), provider);
}

/**
 * Microsoft / Entra ID sign-in.
 *
 * Present only when the deployment advertises it (`providers.microsoft` in
 * /config.json), because it needs an Azure app registration that lives outside
 * this repo — a button that cannot work is worse than no button. Counsel and
 * audit firms are overwhelmingly Microsoft shops, which is the whole reason it
 * exists.
 */
export async function signInWithMicrosoft() {
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(getFirebaseAuth(), provider);
}

/**
 * Create an account with an email address and password.
 *
 * Signing up grants no access to anything. Authorization comes from
 * company_members in Postgres, so a new account with no invitation sees an empty
 * company list — which is why self-service signup is safe to leave open. It
 * exists so an invited person can get in without an administrator having to
 * provision a credential for them out of band.
 *
 * Firebase sends the verification email; the API refuses unverified addresses,
 * and App.jsx renders that gate.
 */
export async function signUpWithPassword(email, password, displayName = null) {
  const credential = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
  if (displayName) {
    await updateProfile(credential.user, { displayName });
  }
  // Sent immediately rather than on first API rejection, so the message is
  // already waiting by the time they read the "verify your email" screen.
  await sendEmailVerification(credential.user);
  return credential;
}

export async function sendPasswordReset(email) {
  return sendPasswordResetEmail(getFirebaseAuth(), email);
}

export async function resendVerification() {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('not signed in');
  await sendEmailVerification(user);
}

export async function signOutNow() {
  await signOut(getFirebaseAuth());
}

/**
 * A current ID token. Firebase caches and refreshes it, so this is cheap to call
 * per request — and calling it per request is what lets a revoked session stop
 * working promptly rather than at the next reload.
 */
export async function getIdToken(forceRefresh = false) {
  const user = getFirebaseAuth().currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/** Friendly text for the Firebase error codes people actually hit. */
export function describeAuthError(err) {
  const code = err?.code ?? '';
  const messages = {
    'auth/invalid-email': 'That email address is not valid.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account exists for that email address.',
    'auth/wrong-password': 'Incorrect email or password.',
    'auth/invalid-credential': 'Incorrect email or password.',
    'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
    'auth/email-already-in-use':
      'An account already exists for that address. Sign in instead, or reset the password.',
    'auth/weak-password': 'Choose a password of at least 8 characters.',
    'auth/missing-password': 'Enter a password.',
    // Raised when the address was registered through Google or Microsoft: there
    // is no password to check. Naming the provider is the whole point — "wrong
    // password" would be a lie and would send someone to reset a password that
    // does not exist.
    'auth/account-exists-with-different-credential':
      'That address already signs in with Google or Microsoft. Use that button instead.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/popup-blocked': 'Your browser blocked the sign-in popup. Allow popups and retry.',
    'auth/unauthorized-domain':
      'This hostname is not an authorized domain on the Firebase project.',
    'auth/operation-not-allowed':
      'That sign-in method is not enabled on the Firebase project. Try another one.',
    // Firebase's catch-all. It has now covered three unrelated real failures on
    // this deployment, none of them named by the SDK: a disabled provider,
    // COOP: same-origin severing the popup's window.opener, and a CSP that
    // blocked apis.google.com so the SDK never issued a request at all.
    //
    // Rather than guess between them, point at the technical detail below — the
    // presence or absence of serverResponse there distinguishes a project
    // misconfiguration from a browser-policy problem in one glance.
    'auth/internal-error':
      'Sign-in failed inside Firebase, which did not say why. Open the technical ' +
      'detail below and send it to an administrator — a "serverResponse" line ' +
      'means a project setting, and no such line means a browser policy on this ' +
      'site. Retrying once is worth a try; retrying repeatedly is not.',
  };
  return messages[code] ?? err?.message ?? 'Sign-in failed.';
}

/**
 * The raw failure detail, for display behind a disclosure and in the console.
 *
 * `auth/internal-error` is a catch-all whose friendly text can only guess. The
 * SDK does carry the underlying cause — the Identity Toolkit response body hangs
 * off `customData.serverResponse` — but the code alone names nothing, so a
 * misconfiguration and a browser-policy problem read identically. Two rounds of
 * misdiagnosis here were spent on causes that turned out to be already correct.
 *
 * Surfacing this is safe: it is a client-side error about the caller's own
 * sign-in attempt, containing no other party's data and no secret. Guessing from
 * an opaque code costs a deploy per hypothesis; showing the detail costs nothing.
 */
export function authErrorDetail(err) {
  if (!err) return null;

  const parts = [];
  if (err.code) parts.push(`code: ${err.code}`);
  if (err.message && err.message !== err.code) parts.push(`message: ${err.message}`);

  // Identity Toolkit's actual response — usually {"error":{"code":400,"message":"…"}}.
  const server = err.customData?.serverResponse ?? err.serverResponse;
  if (server) {
    let text;
    try {
      text = typeof server === 'string' ? server : JSON.stringify(server);
    } catch {
      text = String(server);
    }
    parts.push(`serverResponse: ${text}`);
  }

  // Which of the two failure surfaces we were on when it broke.
  parts.push(`origin: ${window.location.origin}`);
  const domain = runtimeConfig?.firebase?.authDomain;
  if (domain) parts.push(`authDomain: ${domain}`);

  return parts.length ? parts.join('\n') : null;
}
